import React, { useState } from 'react'
import { Heading, Text, Button } from '@primer/react-brand'

import PlanSummaryCard from '../components/PlanSummaryCard'
import { useCurrentOrganization } from '../../components/useCurrentOrganization'
import { API_BASE_URL } from '../../config/apiConfig'
import { APPLICATION_REVIEW_WINDOW } from '../../components/subscriptionConstants'

// Screen 6 — Apply for access. Reached after a card is saved on Screen 5b.
// Shows the org's details read-only for confirmation and submits the application.
// Design: ApplyScreen in Prototype C_A_primer_v2.html (.apply-* classes).
//
// Renders content-only into the header-only app chrome (civic_lens_subscribe
// layout); no page Header/Footer of its own.
//
// Props: { returnTo }
const ApplyForAccessPage = ({ returnTo }) => {
  const { org, loading } = useCurrentOrganization()
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // Loading shell.
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 40px' }}>
        <Text as="p" variant="muted">Loading…</Text>
      </div>
    )
  }

  // Bypass: an already-approved org has no reason to request approval again —
  // send it straight into the app. Checked before the saved-card guard so it
  // applies regardless of whether a card was saved.
  if (org && org.approved_at) {
    if (typeof window !== 'undefined') window.location.href = '/monitoring'
    return null
  }

  // An org that has already requested approval shouldn't re-submit the form —
  // route it to the already-requested confirmation (mirrors the server guard in
  // MainController#subscribe_apply).
  if (org && (org.applied_at || org.status === 'application_pending')) {
    if (typeof window !== 'undefined') window.location.href = '/subscribe/requested'
    return null
  }

  // Guard: no saved card means the user skipped Screen 5b — back to pricing. No
  // plan means the card was recorded without one (the webhook backstop does this
  // when the browser's save request was lost) — also back to pricing, since the
  // server refuses an application with no plan.
  if (!org || !org.payment_method_saved_at || !org.selected_plan) {
    if (typeof window !== 'undefined') window.location.href = '/membership'
    return null
  }

  const plan = (org.selected_plan || '').toLowerCase()
  const fields = [
    { label: 'Organization', value: org.name },
    { label: 'Address', value: org.address },
    { label: 'Domain', value: org.domain || org.url },
    { label: 'Type', value: org.organization_type },
    { label: 'Description', value: org.describe_work },
    { label: 'Intended use', value: org.reason },
  ]
  const missingFields = fields.some((f) => !f.value)
  // The first four fields sit in a two-column grid; Description and Intended use span full width.
  const wideFields = ['Description', 'Intended use']

  const steps = [
    <>You confirm your organization&rsquo;s details below and click Apply for Access. <strong style={{ color: 'var(--base-color-scale-gray-9)', fontWeight: 700 }}>You will not be charged</strong> until your application is approved.</>,
    <>A CivicLens team member will review your application within {APPLICATION_REVIEW_WINDOW}. <strong style={{ color: 'var(--base-color-scale-gray-9)', fontWeight: 700 }}>Your free trial continues</strong> until your application is approved.</>,
    <>Once approved, <strong style={{ color: 'var(--base-color-scale-gray-9)', fontWeight: 700 }}>you&rsquo;ll be billed</strong> and your subscription will begin.</>,
  ]

  const handleApply = async () => {
    if (submitting || missingFields) return
    setSubmitting(true)
    setError(null)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(`${API_BASE_URL}/api/v2/organizations/submit_application`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf,
        },
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.success) {
        window.location.href = returnTo || data.redirect_to || '/monitoring'
      } else {
        setError('Something went wrong submitting your application. Please try again.')
        setSubmitting(false)
      }
    } catch (err) {
      setError('Something went wrong submitting your application. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <div style={{ maxWidth: 960, width: '100%', margin: '0 auto', padding: '52px 40px 96px', fontFamily: 'var(--brand-body-fontFamily)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 56, alignItems: 'flex-start' }}>
          {/* Left — plan summary card (shared with the Payment screen; no footer,
              so both screens render an identical panel) */}
          <PlanSummaryCard plan={plan} />

          {/* Right — application details */}
          <div>
            <Heading as="h1" size="5">Approval needed to proceed</Heading>
            <Text as="p" size="300" variant="muted" style={{ marginTop: 6, marginBottom: 32, lineHeight: 1.65 }}>
              A CivicLens subscription is only available to eligible organizations approved by project staff. Approval is granted when an organization&rsquo;s use of CivicLens supports our mission to strengthen civic life in America.
            </Text>

            {/* What happens next — numbered steps */}
            <div style={{ marginBottom: 22 }}>
              <Text as="p" weight="semibold" style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: 'var(--base-color-scale-gray-9)' }}>
                How to apply for access
              </Text>
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
                {steps.map((step, index) => (
                  <li key={index} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                    <span
                      style={{
                        flexShrink: 0,
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: 'var(--base-color-scale-blue-5)',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ fontSize: 14, color: 'var(--base-color-scale-gray-7)', lineHeight: 1.5, paddingTop: 3 }}>
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Organization details — two-column gray panel */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px 28px',
                background: 'var(--base-color-scale-gray-0)',
                border: '1px solid var(--base-color-scale-gray-1)',
                borderRadius: 8,
                padding: '20px 22px',
                marginBottom: 52,
              }}
            >
              {fields.map((field) => (
                <div key={field.label} style={{ gridColumn: wideFields.includes(field.label) ? '1 / -1' : undefined }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--base-color-scale-gray-6)',
                      display: 'block',
                      marginBottom: 2,
                    }}
                  >
                    {field.label}
                  </span>
                  <div style={{ fontSize: 14.5, color: 'var(--base-color-scale-gray-9)', lineHeight: 1.5 }}>
                    {field.value || '—'}
                  </div>
                </div>
              ))}
            </div>

            {missingFields && (
              <Text as="p" size="100" style={{ margin: '0 0 14px', color: 'var(--brand-color-error-fg, #cf222e)' }}>
                Some organization details are missing. Please add them before applying.
              </Text>
            )}
            {error && (
              <Text as="p" size="100" style={{ margin: '0 0 14px', color: 'var(--brand-color-error-fg, #cf222e)' }}>
                {error}
              </Text>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <Button
                variant="primary"
                onClick={handleApply}
                disabled={submitting || missingFields}
                style={{
                  backgroundColor: 'var(--base-color-scale-blue-5)',
                  borderColor: 'var(--base-color-scale-blue-5)',
                  borderRadius: '6px',
                }}
              >
                {submitting ? 'Submitting…' : 'Apply for Access'}
              </Button>
              <a
                href={`/organizations/${org.id}/edit`}
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--base-color-scale-gray-6)',
                  textDecoration: 'none',
                  borderBottom: '1px solid var(--base-color-scale-gray-1)',
                  paddingBottom: 1,
                }}
              >
                Edit organization details
              </a>
            </div>

            {/* Payment-safety note */}
            <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginTop: 12, maxWidth: 460 }}>
              <svg width="12" height="14" viewBox="0 0 11 13" fill="none" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
                <rect x="0.75" y="5.5" width="9.5" height="7" rx="1.2" stroke="var(--base-color-scale-gray-4)" strokeWidth="1.1" />
                <path d="M3 5.5V4a2.5 2.5 0 0 1 5 0v1.5" stroke="var(--base-color-scale-gray-4)" strokeWidth="1.1" strokeLinecap="round" />
              </svg>
              <Text as="p" style={{ margin: 0, fontSize: 12.5, color: 'var(--base-color-scale-gray-6)', lineHeight: 1.55 }}>
                Changes to your payment method will be saved. <strong style={{ color: 'var(--base-color-scale-gray-9)', fontWeight: 600 }}>You will not be charged until your application is approved.</strong> If your application is not approved, no charge will be made.
              </Text>
            </div>
          </div>
        </div>
      </div>
  )
}

export default ApplyForAccessPage
